import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectSearchOption } from '../models/select-search-option';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-select-search',
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectSearchComponent),
      multi: true,
    },
  ],
})
export class SelectSearchComponent implements OnInit, ControlValueAccessor {
  private translate = inject(TranslateService);

  @Input()
  public label = this.translate.instant('common.search');
  @Input()
  public placeholder = this.translate.instant('common.typeToSearch');
  @Input()
  public disabled = false;
  @Input()
  public required = false;
  @Input()
  public allowCustom = false;
  @Input()
  public clearable = true;
  @Input()
  public noResultsText = this.translate.instant('common.noResults');
  /** Maximale Anzahl angezeigter Vorschläge. 0 = unbegrenzt. */
  @Input()
  public maxResults = 50;
  private currentValue: any = null;
  private optionItems: SelectSearchOption[] = this.createDefaultItems();

  @Input()
  public set items(items: SelectSearchOption[]) {
    this.optionItems = items ?? [];
    this.writeValue(this.currentValue);
  }

  public get items(): SelectSearchOption[] {
    return this.optionItems;
  }

  @Output()
  public selectedItemChanged: EventEmitter<SelectSearchOption | null> =
    new EventEmitter<SelectSearchOption | null>();

  public searchTerm = '';
  public filteredItems: SelectSearchOption[] = [];
  public isOpen = false;
  public highlightedIndex = -1;
  public selectedItem: SelectSearchOption | null = null;

  public get hasSelection(): boolean {
    return this.selectedItem !== null;
  }

  public get hasExactMatch(): boolean {
    const term = this.searchTerm.trim().toLowerCase();
    return this.items.some((item) => item.label.trim().toLowerCase() === term);
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  private createDefaultItems(): SelectSearchOption[] {
    return DUMMY_ITEM_KEYS.map((key, index) => ({
      value: index + 1,
      label: this.translate.instant(key),
    }));
  }

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.filter();
  }

  public writeValue(value: any): void {
    this.currentValue = value;
    const match =
      this.items.find((item) => item.value === value) ??
      (this.allowCustom && typeof value === 'string' && value.length > 0
        ? { value, label: value }
        : null);
    this.selectedItem = match;
    this.searchTerm = match?.label ?? '';
    this.filter();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInput(event: CustomEvent): void {
    this.searchTerm = ((event.detail as any)?.value ?? '') as string;
    this.isOpen = true;
    this.filter();
    this.highlightedIndex = this.filteredItems.length ? 0 : -1;

    // Freitext entspricht keiner Auswahl mehr
    if (this.allowCustom) {
      const exactMatch = this.items.find(
        (item) =>
          item.label.toLowerCase() === this.searchTerm.trim().toLowerCase()
      );
      const customValue = this.searchTerm.trim()
        ? { value: this.searchTerm, label: this.searchTerm }
        : null;
      this.setSelection(exactMatch ?? customValue);
    } else if (
      this.selectedItem &&
      this.selectedItem.label !== this.searchTerm
    ) {
      this.setSelection(null);
    }
  }

  public onFocus(): void {
    this.isOpen = true;
    this.filter();
  }

  public preventInputFocus(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public toggleOptions(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen) {
      this.close();
    } else {
      this.onFocus();
    }
  }

  public onBlur(): void {
    this.onTouched();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.isOpen = true;
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter':
        if (this.isOpen && this.highlightedIndex >= 0) {
          event.preventDefault();
          this.select(this.filteredItems[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  public select(item: SelectSearchOption): void {
    if (item.disabled) {
      return;
    }
    this.searchTerm = item.label;
    this.setSelection(item);
    this.filter();
    this.close();
  }

  public clear(): void {
    this.searchTerm = '';
    this.setSelection(null);
    this.filter();
    this.isOpen = true;
  }

  public selectCustomValue(): void {
    const value = this.searchTerm.trim();
    if (!value) {
      return;
    }
    this.searchTerm = value;
    this.setSelection({ value, label: value });
    this.filter();
    this.close();
  }

  public close(): void {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  public isSelected(item: SelectSearchOption): boolean {
    return item.value === this.selectedItem?.value;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  private setSelection(item: SelectSearchOption | null): void {
    this.selectedItem = item;
    this.onChange(item ? item.value : null);
    this.selectedItemChanged.emit(item);
  }

  private move(delta: number): void {
    if (!this.filteredItems.length) {
      this.highlightedIndex = -1;
      return;
    }
    const next = this.highlightedIndex + delta;
    this.highlightedIndex =
      next < 0
        ? this.filteredItems.length - 1
        : next % this.filteredItems.length;
  }

  private filter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const matches = term
      ? this.items.filter((item) => item.label.toLowerCase().includes(term))
      : [...this.items];
    this.filteredItems =
      this.maxResults > 0 ? matches.slice(0, this.maxResults) : matches;
  }
}

const DUMMY_ITEM_KEYS = [
  'machinery.examples.excavator',
  'machinery.examples.wheelLoader',
  'machinery.examples.miniExcavator',
  'machinery.examples.roller',
  'machinery.examples.vibratingPlate',
  'machinery.examples.crawlerExcavator',
  'machinery.examples.telehandler',
  'machinery.examples.dumpTruck',
  'machinery.examples.towerCrane',
  'machinery.examples.concreteMixer',
  'machinery.examples.generator',
  'machinery.examples.siteContainer',
];
